import { PeriodBehavior } from '@fabric-ui/components';
import z from 'zod/v4';
import {
  isValueExpr,
  isValueExprList,
  SupportedFunction,
  type ValueExpr,
  type ValueExprList,
  type Values,
} from './SqlToFilterConverter.utils';

const valueParser = (valueExpr: ValueExpr): Values => {
  switch (valueExpr.type) {
    case 'bigint':
    case 'number':
      return z.coerce.number().parse(valueExpr.value);

    case 'string':
    case 'backticks_quote_string':
    case 'double_quote_string':
    case 'single_quote_string':
      return z.coerce.string().parse(valueExpr.value);

    case 'boolean':
    case 'bool':
      return z.coerce.boolean().parse(valueExpr.value);

    case 'null':
      return z.null().parse(valueExpr.value);

    case 'date':
      z.coerce.date().parse(valueExpr.value);
      return valueExpr.value;

    default:
  }

  valueExpr satisfies never;
  throw new Error(`Unsupported value type: ${JSON.stringify(valueExpr)}`);
};

const parseValueExpr = (value: ValueExpr): Values => {
  try {
    return valueParser(value);
  } catch (error) {
    throw new Error(`Failed to parse value expression. type: ${value.type} value: ${value.value}`, { cause: error });
  }
};
const parseValueExprList = ({ value }: ValueExprList): Values[] => value.map(parseValueExpr);

export const parseValue = (value: ValueExpr | ValueExprList): Values | Values[] => {
  if (isValueExpr(value)) {
    return parseValueExpr(value);
  }
  if (isValueExprList(value)) {
    return parseValueExprList(value);
  }

  throw new Error(`Expected ValueExpr or ValueExprList`);
};

const functionArgumentsParseMap = {
  RELATIVE_DATE: z.tuple([
    z.enum(PeriodBehavior),
    z.coerce.number().nonnegative(),
    z.enum(['MINUTES', 'HOURS', 'DAYS', 'WEEKS', 'MONTHS']),
  ]),
} as const satisfies Record<SupportedFunction, unknown>;

export const parseFunctionArguments = (functionName: SupportedFunction, args: unknown[]) => {
  try {
    return functionArgumentsParseMap[functionName].parse(args);
  } catch (e) {
    throw new Error(`Failed to parse arguments for function ${functionName}`, { cause: e });
  }
};
